import React, { useContext } from 'react'
import type { Genre, SocialMediaLinks } from '../../../grab/types'
import { MainStylesVars, StyleSheet } from '../stylesheet'
import { Title } from '../components/title'
import { useTranslation } from 'react-i18next'
import { BadgeList, Card, HorizontalBar, IconBtn, IconText, NavigationLink, SocialsList } from '../components/components-lib'
import { MainConfig, useLang } from '../chapito-static'
import { Collapse, ConfigProvider } from 'antd'
import type { GroupOrEvent } from '../components/guest-or-event-card'
import { GuestOrEventCard } from '../components/guest-or-event-card'
import { ResourceImage } from '../components/resource-image'
import { YoutubeEmbed } from '../components/youtube-embed'
import { SpotifyEmbed } from '../components/spotify-embed'
import { DeezerEmbed } from '../components/deezer-embed'
import { LangSwitchDropdown } from '../components/lang-switch-dropdown'
import type { StaticGroup, StaticEvent, DetailLayoutConfig } from '../types'
import { DetailLayout } from '../components/detail-layout'
import { isNotNullish, keys } from '../../../services/typescript'
import { faAngleLeft, faEnvelope, faMarker, faPhone, faUser } from '@fortawesome/free-solid-svg-icons'
import { groupTagsByCategory } from '../../../services/tags'
import css from './guest-or-event.css'

const GuestInfo: React.FC<{ guest: StaticGroup }> = ({ guest }) => {
  const { t } = useTranslation()

  return (
    <>
      <DescriptionSection description={guest.description} genres={guest.genres} />
      <DescriptionSection description={guest.lineUp} />
      {guest.links?.youtubeVideoKey && <YoutubeSection videoKey={guest.links.youtubeVideoKey} />}
      <div className={css['two-cols']}>
        <ArtistLinksSection {...guest} />
      </div>
      <SocialsSection {...guest} />
      {guest.copyright ? (
        <div style={styles.copyright}>
          {t('ARTIST.COPYRIGHT')} @ {guest.copyright}
        </div>
      ) : null}
    </>
  )
}

export const GuestPage: React.FC<{ guest: StaticGroup; events: StaticEvent[]; detailLayout?: DetailLayoutConfig }> = ({
  guest,
  events,
  detailLayout,
}) => {
  // If a custom detail layout is configured, use it
  if (detailLayout && (detailLayout.banner || detailLayout.columns.length > 0)) {
    return (
      <DetailPageWrapper>
        <DetailLayout layout={detailLayout} guest={guest} />
      </DetailPageWrapper>
    )
  }

  // Otherwise, use the default layout
  return (
    <GuestOrEventPage
      primaryGuestOrEvent={guest}
      primaryEvent={events.length > 0 ? events[0] : undefined}
      otherEvents={events.slice(1)}
      spotifyLink={guest.links?.spotify}
      deezerLink={guest.links?.deezer}
    >
      <GuestInfo guest={guest} />
    </GuestOrEventPage>
  )
}

export const EventPage: React.FC<{
  event: StaticEvent
  guests: StaticGroup[]
  relatedEvents: StaticEvent[]
  detailLayout?: DetailLayoutConfig
}> = ({ event, guests, relatedEvents, detailLayout }) => {
  const config = useContext(MainConfig)

  // If a custom detail layout is configured, use it
  if (detailLayout && (detailLayout.banner || detailLayout.columns.length > 0)) {
    return (
      <DetailPageWrapper>
        <DetailLayout layout={detailLayout} event={event} />
      </DetailPageWrapper>
    )
  }

  // Otherwise, use the default layout
  const guestsByType = guests.reduce<Record<string, StaticGroup[]>>((acc, guest) => {
    acc[guest.guestType.name] = [...(acc[guest.guestType.name] || []), guest]
    return acc
  }, {})
  return (
    <GuestOrEventPage
      primaryGuestOrEvent={event}
      primaryEvent={event}
      otherEvents={relatedEvents}
      spotifyLink={guests.length === 1 ? guests[0].links?.spotify : undefined}
      deezerLink={guests.length === 1 ? guests[0].links?.deezer : undefined}
    >
      <DescriptionSection description={event.description} genres={event.genres} />
      {Object.entries(guestsByType).map(([type, guests]) => (
        <div key={type}>
          <Title level={3}>{type}</Title>
          <ConfigProvider
            theme={{
              token: {
                colorTextBase: config.colorBlockText,
                colorBgContainer: config.colorBackground,
                colorBorder: config.colorBackground,
              },
            }}
          >
            <Collapse
              items={guests.map((guest, index) => ({ ...guestSections(guest), index }))}
              bordered={true}
              style={{ backgroundColor: config.colorBlockBackground }}
              rootClassName={css.collapseRoot}
            />
          </ConfigProvider>
        </div>
      ))}
    </GuestOrEventPage>
  )
}

// Wrapper for custom detail layout pages with navigation header
const DetailPageWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation()

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <NavigationLink path={{ page: 'home' }}>
          <IconBtn type="link" icon={faAngleLeft} style={{ border: `1px solid ${MainStylesVars.colorAction}`, color: MainStylesVars.colorAction }}>
            {t('GENERAL.NAV_GO_BACK')}
          </IconBtn>
        </NavigationLink>
        <LangSwitchDropdown />
      </div>
      <div style={styles.body}>{children}</div>
    </div>
  )
}

const GuestOrEventPage: React.FC<
  React.PropsWithChildren<{
    primaryGuestOrEvent: GroupOrEvent
    primaryEvent: StaticEvent | undefined
    otherEvents: StaticEvent[]
    spotifyLink?: string | undefined
    deezerLink?: string | undefined
  }>
> = ({ primaryGuestOrEvent, primaryEvent, otherEvents, spotifyLink, deezerLink, children }) => {
  const { t } = useTranslation()

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <NavigationLink path={{ page: 'home' }}>
          <IconBtn type="link" icon={faAngleLeft} style={{ border: `1px solid ${MainStylesVars.colorAction}`, color: MainStylesVars.colorAction }}>
            {t('GENERAL.NAV_GO_BACK')}
          </IconBtn>
        </NavigationLink>
        <LangSwitchDropdown />
      </div>

      <div style={styles.body} className={css.guestOrEventPageBody}>
        <div style={styles.columnLeft} className={css.guestOrEventPageColumnLeft}>
          <GuestOrEventCard groupOrEvent={primaryGuestOrEvent} event={primaryEvent} display="large" />
          {otherEvents.length > 0 && <Title level={2}>{t('ARTIST.OTHER_EVENTS')}</Title>}
          {otherEvents.map((ev) => (
            <GuestOrEventCard key={ev.id} groupOrEvent={ev} event={ev} display="small" forceShowHour />
          ))}
          {spotifyLink && <SpotifyEmbed spotifyArtistLink={spotifyLink} style={{ height: 400 }} />}
          {deezerLink && <DeezerEmbed deezerLink={deezerLink} style={{ height: 400 }} />}
        </div>

        <div style={styles.columnRight}>{children}</div>
      </div>
    </div>
  )
}

const ArtistLinks: React.FC<Pick<StaticGroup, 'email' | 'phone' | 'address' | 'contact'>> = ({ email, phone, address, contact }) => {
  return [
    contact ? <IconText icon={faUser} value={contact}></IconText> : null,
    phone ? <IconText icon={faPhone} href={`tel:${phone}`} value={phone}></IconText> : null,
    email ? <IconText icon={faEnvelope} href={`mailto:${email}`} value={email}></IconText> : null,
    address ? <IconText icon={faMarker} value={address}></IconText> : null,
  ]
    .filter(isNotNullish)
    .reduce((acc, value, index) => {
      return (
        <>
          {acc}
          {index !== 0 ? <HorizontalBar style={{ marginTop: 3, marginBottom: 3, marginLeft: 40 }} /> : null}
          {value}
        </>
      )
    })
}

const GuestTags: React.FC<{ genres: Genre[] }> = ({ genres }) => {
  const lang = useLang()
  const groupedTags = groupTagsByCategory(genres, { lang })

  return (
    <>
      {groupedTags.map(({ category, genres, key }) => (
        <div key={key} style={styles.tagsContainer}>
          <div style={styles.tagsCategoryTitle}>{category.name}</div>
          <BadgeList labels={genres.map((g) => g.name)} />
        </div>
      ))}
    </>
  )
}

const DescriptionSection: React.FC<{ description: string | undefined; genres?: Genre[] }> = ({ description, genres }) => {
  const { t } = useTranslation()

  return description ? (
    <>
      <Title level={3}>{t('ARTIST.A_PROPOS')}</Title>
      <Card>
        {genres && <GuestTags genres={genres} />}
        <div dangerouslySetInnerHTML={{ __html: description }}></div>
      </Card>
    </>
  ) : (
    <></>
  )
}

const ArtistLinksSection: React.FC<Pick<StaticGroup, 'email' | 'phone' | 'address' | 'contact'>> = (props) => {
  const { t } = useTranslation()
  return props.email || props.phone || props.address || props.contact ? (
    <div>
      <Title level={3}>{t('ARTIST.LINKS')}</Title>
      <Card style={styles.artistLinksCard}>
        <ArtistLinks {...props} />
      </Card>
    </div>
  ) : (
    <></>
  )
}

const YoutubeSection: React.FC<{ videoKey: NonNullable<SocialMediaLinks['youtubeVideoKey']> }> = ({ videoKey }) => {
  const { t } = useTranslation()
  return (
    <>
      <Title level={3}>{t('ARTIST.VIDEO')}</Title>
      <YoutubeEmbed videoId={videoKey} />
    </>
  )
}

const SocialsSection: React.FC<Pick<StaticGroup, 'links' | 'name'>> = ({ links, name }) => {
  const lang = useLang()
  const { t } = useTranslation()

  return links && keys(links).some((k) => !!links[k]) ? (
    <>
      <Title level={3}>{t('ARTIST.SOCIALS', { artist: name.toLocaleUpperCase(lang) })}</Title>
      <SocialsList socials={links} />
    </>
  ) : (
    <></>
  )
}

const guestSections = (guest: StaticGroup): { label: JSX.Element; children: JSX.Element } => {
  const label = (
    <div style={{ display: 'flex', alignItems: 'center', color: MainStylesVars.colorBlockAction }}>
      {guest.image && <ResourceImage imageId={guest.image} alt="" style={{ maxHeight: 30, marginRight: 10 }} />}
      <div>{guest.name}</div>&nbsp;
      <div>{guest.location}</div>
    </div>
  )

  const children = (
    <div style={styles.columnRight}>
      <GuestInfo guest={guest} />
    </div>
  )
  return { label, children }
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },

  body: {
    display: 'flex',
    width: '100%',
    gap: 20,
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 30,
  },

  columnLeft: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },

  columnRight: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    flexBasis: 2,
  },

  artistLinksCard: {
    marginTop: 12,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 15,
    paddingRight: 15,
  },

  guestSection: {
    marginBottom: 10,
    color: MainStylesVars.colorTitle,
  },

  copyright: {
    color: MainStylesVars.colorTitle,
  },

  tagsContainer: {
    marginBottom: 15,
  },

  tagsCategoryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    paddingBottom: 8,
    color: MainStylesVars.colorBlockAction,
  },
})
